import React, { useState, useEffect, useMemo } from "react";
import { Tooltip, IconButton, TablePagination } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import Helmet from "react-helmet";

import {
    Portlet,
    PortletBody,
    PortletHeader,
    PortletHeaderToolbar,
} from "../../../partials/content/Portlet";
import { DataFilter, DataTable, TablePaginationActions } from "../../../common";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { reorderArray } from "../../../util";
import { adjustCurrentPage } from "../../../util";
import {
    rcmAssignmentLanding,
    changePrecedence,
    rcmAssignmentCreateUpdateDialog,
    rcmAssignmentDeleteDialog,
} from "../../../store/actions/rcmAssignment";
import { set } from "../../../store/actions/pages";
import { StringEquators } from "../../../store/constants/pages";
import {
    createIconDataField,
    createStringDataField,
} from "../../../util/format";
import { pageStore } from "../../../common";
import { RootState } from "../../../store/reducers";
import { useDispatch, useSelector } from "react-redux";
import { sortData, filterData } from "../../../util/selector";
import CreateNewRCMStatusDialog from "../RCMAssignmentDialogs/CreateNewRCMStatusDialog";
import RcmDeleteDependencyDialog from "../RcmDeleteDependency/RcmDeleteDependencyDialog";

const useStyles = makeStyles((theme) => ({
    root: {
        margin: "auto",
    },
    button: {
        marginTop: theme.spacing(3),
        marginBottom: theme.spacing(1),
    },
    icon: {
        fontSize: 17,
        marginTop: 1,
    },
    textField: {
        width: 400,
    },
}));

const DEFAULT_SORT = [{ id: "precedence", direction: "asc" }];

const { usePageStore, useGridFilters } = pageStore(
    "rcm-assignment-landing",
    {}
);

export const RCMAssignmentCreate = () => {
    const fields = useMemo(
        () => [
            createStringDataField("name", "Display Name", {
                sortable: false,
                equators: StringEquators,
                whiteSpace: "break-spaces",
            }),
            createStringDataField("type", "Assignment Type", {
                sortable: false,
                equators: StringEquators,
                whiteSpace: "break-spaces",
            }),
        ],
        []
    );

    const rcmAssignmentData: any = useSelector<RootState>(
        (state) => state?.rcmAssignment?.data
    );
    const rcmChangePrecedencePending: any = useSelector<RootState>(
        (state) => state.rcmAssignment.changePrecedence.pending
    );

    const pendingRcmAssignmentFetch: any = useSelector<RootState>(
        (state) => state?.rcmAssignment?.list?.pending
    );

    const pageSort = usePageStore((s: any) => {
        return s?.sort || [];
    });

    const pageFilter = usePageStore((s: any) => {
        return s?.filter || null;
    });

    const filteredFields = fields.map((field) => field.id);

    const order = usePageStore((s: any) => {
        return s?.order || null;
    });

    const structuredData = () => {
        const filteredData = filterData(
            filteredFields,
            pageFilter,
            rcmAssignmentData
        );
        const filteredDataWithSortApplied = sortData(
            filteredFields,
            pageSort,
            filteredData
        );

        return filteredDataWithSortApplied || [];
    };
    const data = structuredData();

    const dispatch = useDispatch();
    const classes = useStyles();
    const [rowsPerPage, setRowsPerPage] = useState(50);
    const [page, setPage] = useState(0);
    const [tempRCM, setTempRCM] = useState([]);

    useEffect(() => {
        setTempRCM([...data]);
    }, [rcmAssignmentData]);

    useEffect(() => {
        let currentPage = page + 1;
        let totalRecords = data?.length;
        let pageSize = rowsPerPage;

        currentPage = adjustCurrentPage(currentPage, totalRecords, pageSize);
        if (page === currentPage - 1) return;
        setPage(currentPage - 1);
    }, [data]);

    useEffect(() => {
        dispatch(rcmAssignmentLanding());
        return () => {
            // dispatch(rcmAssignmentLanding());
            setFilter("");
        };
    }, []);

    const handleRefresh = () => {
        dispatch(rcmAssignmentLanding());
    };

    const handleSearchInput = (value) => {
        setFilter(value);
    };

    const handleAddRcmAssignment = () => {
        dispatch(
            rcmAssignmentCreateUpdateDialog({ open: true, rowData: null })
        );
    };

    const handleEditRcmAssignment = (rowData) => {
        dispatch(rcmAssignmentCreateUpdateDialog({ open: true, rowData }));
    };

    const handleDeleteRcmTeam = (rowData) => {
        dispatch(
            rcmAssignmentDeleteDialog({
                open: true,
                rowData: { id: rowData?.id, name: rowData?.name },
            })
        );
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleDragAndDrop = (prevIndex, newIndex) => {
        const reorderedData = reorderArray(tempRCM, prevIndex, newIndex);
        setTempRCM(reorderedData);
        handleChangePrecedence(reorderedData);
    };

    const handleChangePrecedence = (rcmTeamsArray) => {
        const formattedData = rcmTeamsArray.map((status, index) => {
            return {
                rcmTeamId: status.id,
                precedence: index + 1,
            };
        });
        dispatch(
            changePrecedence(
                { rcmTeamPrecedences: formattedData },
                handleRefresh
            )
        );
    };

    const setFilter = (value) =>
        dispatch(set(`rcm-assignment-landing`, "filter", value));
    const setOrder = (value) =>
        dispatch(set(`rcm-assignment-landing`, "order", value));
    const setSort = (value) =>
        dispatch(set(`rcm-assignment-landing`, "sort", value));

    function rcmTeamActions(rowData) {
        return (
            rowData?.type !== "Default" && (
                <div className="d-flex">
                    <IconButton
                        color="primary"
                        className="p-1"
                        aria-label="Edit"
                        onClick={() => handleEditRcmAssignment(rowData)}
                        size="large"
                    >
                        <Tooltip title="Edit">
                            <EditIcon />
                        </Tooltip>
                    </IconButton>
                    {/* {rowData && rowData.type === "Custom" && ( */}
                    <IconButton
                        color="primary"
                        className="p-1"
                        aria-label="Delete"
                        onClick={() => handleDeleteRcmTeam(rowData)}
                        size="large"
                    >
                        <Tooltip title="Delete">
                            <DeleteIcon />
                        </Tooltip>
                    </IconButton>
                    {/* )} */}
                </div>
            )
        );
    }

    return (
        <>
            <Helmet title="Rcm Teams" />
            <Portlet>
                <PortletHeader
                    title="RCM Teams"
                    handleRefresh={handleRefresh}
                    name="rcm-assignment-landing"
                    showSearchFilters={false}
                    toolbar={
                        <PortletHeaderToolbar>
                            <DataFilter
                                onChange={handleSearchInput}
                                placeholder="Search"
                                cssClass={classes.textField}
                                filter={pageFilter}
                            />
                        </PortletHeaderToolbar>
                    }
                />
                <PortletBody>
                    <div className="row">
                        <div className="col-md-12">
                            <DataTable
                                name="RCM Assignment"
                                fields={fields}
                                data={structuredData()?.slice(
                                    page * rowsPerPage,
                                    page * rowsPerPage + rowsPerPage
                                )}
                                handleRefresh={handleRefresh}
                                defaultSort={DEFAULT_SORT}
                                onAdd={handleAddRcmAssignment}
                                // tableLevelActions={tableLevelActions}
                                loading={
                                    rcmChangePrecedencePending ||
                                    pendingRcmAssignmentFetch
                                }
                                title="rcmAssignment"
                                order={order}
                                onOrder={setOrder}
                                sort={pageSort || DEFAULT_SORT}
                                onSort={setSort}
                                stickyHeader={true}
                                onDragEnd={handleDragAndDrop}
                                isDnDEnabled={true}
                                showPagination={true}
                                footerPagination={() => (
                                    <TablePagination
                                        rowsPerPageOptions={[
                                            10,
                                            50,
                                            100,
                                            250,
                                            500,
                                            { label: "All", value: -1 },
                                        ]}
                                        colSpan={fields.length + 2}
                                        count={data.length}
                                        rowsPerPage={rowsPerPage}
                                        page={page}
                                        SelectProps={{
                                            inputProps: {
                                                "aria-label": "rows per page",
                                            },
                                            native: true,
                                        }}
                                        onPageChange={handleChangePage}
                                        onRowsPerPageChange={
                                            handleChangeRowsPerPage
                                        }
                                        ActionsComponent={
                                            TablePaginationActions
                                        }
                                    />
                                )}
                                hasActions={true}
                                renderActionColumn={rcmTeamActions}
                            />
                        </div>
                    </div>
                </PortletBody>
            </Portlet>
            <CreateNewRCMStatusDialog callBack={handleRefresh} />
            <RcmDeleteDependencyDialog
                rcmAssignmentData={rcmAssignmentData}
                handleClose={() =>
                    dispatch(
                        rcmAssignmentDeleteDialog({
                            open: false,
                            rowData: null,
                        })
                    )
                }
                handleRefresh={handleRefresh}
            />
           
        </>
    );
};
